<template>
  <form class="signup" :class="{ 'is-compact': compact }" @submit.prevent="send">
    <p v-if="!compact" class="signup__pitch">{{ pitch }}</p>

    <!-- Spam trap: hidden from people, but a bot filling every field gives
         itself away. Nothing is written when it is filled. -->
    <label class="signup__trap" aria-hidden="true">
      Leave this field empty
      <input v-model="botField" tabindex="-1" autocomplete="off">
    </label>

    <div class="signup__row">
      <label class="signup__field">
        <span class="signup__label">Email</span>
        <input
          v-model="email"
          type="email"
          inputmode="email"
          autocomplete="email"
          :placeholder="placeholder"
          :disabled="state === 'sending'"
          :aria-invalid="state === 'error' ? 'true' : undefined"
          maxlength="254"
          required
          @input="onEdit"
        >
      </label>

      <button type="submit" class="signup__go" :disabled="state === 'sending' || capped">
        {{ state === 'sending' ? 'Adding…' : 'Subscribe' }}
      </button>
    </div>

    <p v-if="message" class="signup__note" :class="`is-${state}`" role="status">{{ message }}</p>
    <p v-else-if="!compact" class="signup__small">No spam, and unsubscribe whenever you like.</p>
  </form>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

const props = defineProps({
  // Which placement this is, recorded with the signup so it is possible to tell
  // later which one actually earns subscribers.
  source: { type: String, required: true },
  // Tight spaces drop the pitch and the reassurance and keep the field.
  compact: { type: Boolean, default: false },
  pitch: {
    type: String,
    default: 'New writing, straight to your inbox. Nothing else.',
  },
  placeholder: { type: String, default: 'you@example.com' },
});

const email = ref('');
const botField = ref('');

// Two addresses from one browser in one session.
//
// The form used to disable itself the moment one went through, which reads as
// finished and is wrong: somebody with a second inbox has nowhere to put it,
// and somebody who mistyped the first one has to reload the page to try again.
// Two is enough for both of those and not enough to be worth anyone's while.
//
// A courtesy rather than a control. The function behind this is what actually
// refuses a duplicate, and anyone determined can empty their own storage; the
// point is to stop the form being annoying, not to police it. sessionStorage
// because "for this visit" is exactly what it means, and it goes with the tab.
const SESSION_CAP = 2;
const STORE = 'newsletter:sent';

// idle | sending | done | already | limit | error
const state = ref('idle');
const error = ref('');
const sentCount = ref(0);

const capped = computed(() => sentCount.value >= SESSION_CAP);

const message = computed(() => {
  if (state.value === 'done') return "You're on the list. Thanks.";
  if (state.value === 'already') return 'That address is already on the list.';
  if (state.value === 'error') return error.value;
  if (state.value === 'limit' || capped.value) {
    return 'That is two addresses from this browser. Any more can wait for later.';
  }
  return '';
});

function readSent() {
  try {
    const list = JSON.parse(sessionStorage.getItem(STORE) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    // Private mode, or storage turned off.
    return [];
  }
}

function remember(key) {
  const list = readSent();
  if (!list.includes(key)) list.push(key);
  sentCount.value = list.length;

  try {
    sessionStorage.setItem(STORE, JSON.stringify(list));
  } catch {
    // The count still holds for as long as this page is open, and losing it on
    // the next one is the right way for a courtesy to fail.
  }
}

onMounted(() => {
  sentCount.value = readSent().length;
});

// Typing is the reader starting again, so the last answer stops applying.
//
// Bound to the event rather than watching the ref: the field is cleared in code
// after a signup, and that must not count as starting again or the confirmation
// would erase itself the instant it appeared.
function onEdit() {
  if (state.value !== 'sending') state.value = 'idle';
}

async function send() {
  if (state.value === 'sending') return;

  const address = email.value.trim();
  if (!address) return;

  // Case carries no meaning in an address, and the same one typed two ways is
  // still the same one.
  const key = address.toLowerCase();
  const already = readSent();

  // Asked in this session already, so the answer is known and the server does
  // not need troubling for it.
  if (already.includes(key)) {
    state.value = 'already';
    return;
  }

  if (already.length >= SESSION_CAP) {
    state.value = 'limit';
    return;
  }

  state.value = 'sending';
  error.value = '';

  try {
    const response = await fetch('/.netlify/functions/subscribe', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: address, source: props.source, botField: botField.value }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'that did not go through');

    state.value = data.already ? 'already' : 'done';
    // Both answers mean the address is on the list, and both were a submission,
    // so both are remembered and both count against the two.
    remember(key);
    email.value = '';
  } catch (e) {
    state.value = 'error';
    error.value = e.message;
  }
}
</script>

<style lang="scss" scoped>
.signup {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 420px;
}

.signup__pitch {
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.6;
  color: var(--secondary);
}

// Off screen rather than display:none, so a bot that reads the DOM still finds
// it and fills it in.
.signup__trap {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.signup__row {
  display: flex;
  gap: 0.45rem;
  align-items: stretch;

  @media (max-width: 420px) {
    flex-direction: column;
  }
}

.signup__field {
  flex: 1;
  min-width: 0;
  display: flex;
}

// The button beside it says what the field is for, so the label is for screen
// readers rather than for the layout.
.signup__label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
}

.signup__field input {
  width: 100%;
  min-width: 0;
  padding: 0.6rem 0.85rem;
  border: 1px solid rgba(var(--border-rgb), 0.45);
  border-radius: 999px;
  background: transparent;
  font: inherit;
  // Under 16px a phone zooms the page in when the field is focused.
  font-size: 16px;
  color: var(--text);

  &::placeholder { color: var(--muted); }

  &:focus {
    outline: none;
    border-color: rgba(var(--accent-rgb), 0.65);
  }

  &:disabled { opacity: 0.6; }
}

.signup__go {
  flex-shrink: 0;
  // Wide enough for the longest label it ever carries, so it does not resize
  // when "Subscribe" becomes "Adding…". The field beside it is flex: 1 and
  // absorbs whatever this gives up, so a button shrinking by a few pixels made
  // the input jump wider at the exact moment the reader is watching for
  // something to happen. Nothing moves now; only the word changes.
  min-width: 7rem;
  padding: 0.6rem 1.15rem;
  border: none;
  border-radius: 999px;
  background: var(--accent);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  &:hover:not(:disabled) { opacity: 0.88; }
  &:disabled { opacity: 0.55; cursor: default; }
}

.signup__note {
  margin: 0;
  font-size: 0.76rem;
  color: var(--muted);

  &.is-done,
  &.is-already { color: var(--accent); }
  &.is-error { color: #dc2626; }
}

.signup__small {
  margin: 0;
  font-size: 0.7rem;
  color: var(--muted);
}

.signup.is-compact {
  gap: 0.35rem;

  .signup__go { font-size: 0.78rem; }
}
</style>
